import React, { useState } from 'react';
import { api } from '../../services/api';

export const ContactPage: React.FC = () => {
    const [form, setForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        website_url_trap: '',
    });
    const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setStatus(null);
        try {
            await api.submitContact(form);
            setStatus({
                type: 'success',
                message: 'Your message has been delivered directly to Rakib. Thank you for connecting!'
            });
            setForm({ name: '', email: '', subject: '', message: '', website_url_trap: '' });
        } catch (err: any) {
            setStatus({
                type: 'error',
                message: err.message || 'Failed to submit message. Please try again.'
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <section className="py-16 bg-radial-glow border-b border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl space-y-4">
                        <span className="text-xs font-bold text-emerald-400 tracking-wider uppercase">Direct Communication</span>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                            Get in Touch
                        </h1>
                        <p className="text-lg text-slate-300">
                            Have a question, collaboration inquiry, or youth initiative to discuss? Send a direct message below.
                        </p>
                    </div>
                </div>
            </section>

            {/* Content Grid */}
            <section className="py-20 bg-slate-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        
                        {/* Left Info Column */}
                        <div className="lg:col-span-5 space-y-8">
                            <div>
                                <h2 className="text-2xl font-bold text-white mb-2">Direct Contact Channels</h2>
                                <p className="text-sm text-slate-400 leading-relaxed">
                                    Messages sent through this form are securely delivered directly to Rakib's administrative inbox.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="p-5 rounded-2xl glass-card space-y-1">
                                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Email</span>
                                    <p className="text-base font-bold text-white">rakibh6970@gmail.com</p>
                                </div>

                                <div className="p-5 rounded-2xl glass-card space-y-1">
                                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Location</span>
                                    <p className="text-base font-bold text-white">Habiganj, Sylhet, Bangladesh</p>
                                </div>

                                <div className="p-5 rounded-2xl glass-card space-y-1">
                                    <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Official Domain</span>
                                    <p className="text-base font-bold text-white">rakibscare.com</p>
                                </div>
                            </div>

                            <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
                                <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Active Platforms</h3>
                                <p className="text-xs text-slate-400 leading-relaxed">
                                    For event invitations or student club inquiries, feel free to connect via social platforms or leave an email message.
                                </p>
                                <div className="flex items-center gap-4 pt-2">
                                    <a 
                                        href="https://www.facebook.com/share/1CK6QxzTH5/" 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="px-4 py-2 rounded-full text-xs font-bold bg-slate-950 border border-slate-700 text-slate-300 hover:text-emerald-400 hover:border-emerald-500 transition"
                                    >
                                        Facebook &rarr;
                                    </a>
                                    <a 
                                        href="https://wa.me/8801762405538" 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="px-4 py-2 rounded-full text-xs font-bold bg-slate-950 border border-slate-700 text-slate-300 hover:text-emerald-400 hover:border-emerald-500 transition"
                                    >
                                        WhatsApp &rarr;
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Right Form Column */}
                        <div className="lg:col-span-7">
                            <div className="p-8 sm:p-10 rounded-3xl glass-card shadow-2xl">
                                <h2 className="text-2xl font-bold text-white mb-6">Send a Message</h2>

                                {status && (
                                    <div className={`mb-6 p-4 rounded-xl text-sm font-medium ${status.type === 'success' ? 'bg-emerald-900/80 text-emerald-200 border border-emerald-500/40' : 'bg-rose-900/80 text-rose-200 border border-rose-500/40'}`}>
                                        {status.message}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Honeypot */}
                                    <input 
                                        type="text" 
                                        name="website_url_trap" 
                                        value={form.website_url_trap} 
                                        onChange={e => setForm({ ...form, website_url_trap: e.target.value })}
                                        className="hidden" 
                                        tabIndex={-1} 
                                        autoComplete="off" 
                                    />

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="contact-name" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Full Name</label>
                                            <input 
                                                type="text" 
                                                id="contact-name" 
                                                required 
                                                value={form.name}
                                                onChange={e => setForm({ ...form, name: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" 
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="contact-email" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Email Address</label>
                                            <input 
                                                type="email" 
                                                id="contact-email" 
                                                required 
                                                value={form.email}
                                                onChange={e => setForm({ ...form, email: e.target.value })}
                                                className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" 
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="contact-subject" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Subject / Organization</label>
                                        <input 
                                            type="text" 
                                            id="contact-subject" 
                                            required 
                                            value={form.subject}
                                            onChange={e => setForm({ ...form, subject: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" 
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="contact-message" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Message</label>
                                        <textarea 
                                            id="contact-message" 
                                            rows={6} 
                                            required 
                                            value={form.message}
                                            onChange={e => setForm({ ...form, message: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm" 
                                            placeholder="Write your message here..."
                                        />
                                    </div>

                                    <div className="text-right">
                                        <button 
                                            type="submit" 
                                            disabled={submitting}
                                            className="px-8 py-3.5 rounded-full text-sm font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-500 text-white shadow-xl shadow-emerald-950 transition cursor-pointer disabled:opacity-50"
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Message →'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};
